import React, { useEffect, useState } from "react";
import { userService } from "../services/userService";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  LuChevronDown,
  LuMail,
  LuUserRound,
  LuUsersRound,
} from "react-icons/lu";
import companyController from "../controllers/companyController";
import roleController from "../controllers/roleController";
import userController from "../controllers/userController";
import Utils from "../utils/Utils";
import VendorLayout from "./VendorLayout";
import ActiveIcon from "../icons/ActiveIcon";
import UserAddedSuccessIcon from "../icons/UserAddedSuccessIcon";
import { getStoredUserId } from "./vendorSession";
import "./vendorUserManagement.css";
import { translateLabel, useTranslationSnapshot } from "./../translations/runtime";

const editUserFeedbackLabels = new Map([
  ["Company id was not found for this user.", "txt_web_vendor_company_id_was_not_found_for_this_user"],
  ["Unable to load user details.", "txt_web_vendor_unable_to_load_user_details"],
  ["First name and last name can only contain letters.", "txt_web_vendor_first_name_and_last_name_can_only_contain_letters"],
  ["Admin id was not found for this user.", "txt_web_vendor_admin_id_was_not_found_for_this_user"],
  ["User id must be a valid UUID.", "txt_web_vendor_user_id_must_be_a_valid_uuid"],
  ["Birth date is required.", "txt_web_vendor_birth_date_required"],
  ["Phone number is required.", "txt_web_vendor_phone_number_is_required"],
  ["Roles are still loading.", "txt_web_vendor_roles_are_still_loading"],
  ["Role id must be a valid UUID.", "txt_web_vendor_role_id_must_be_a_valid_uuid"],
  ["Unable to update user.", "txt_web_vendor_unable_to_update_user"],
  ["Unable to load roles.", "txt_web_vendor_unable_to_load_roles"],
]);
const translateEditUserFeedback = (message) => {
  const id = editUserFeedbackLabels.get(message);
  return id ? translateLabel(id, message) : message;
};

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent);

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? {};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeLabel = (value) =>
  String(value || "").trim().toLowerCase().replace(/[\s_-]+/g, "");

const isUuid = (value) => UUID_PATTERN.test(String(value || ""));

const toDateInput = (value) => {
  if (!value) return "";

  const stringValue = String(value);
  const dateParts = stringValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (dateParts) {
    return `${dateParts[3]}-${dateParts[2]}-${dateParts[1]}`;
  }

  const date = new Date(stringValue);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

const toApiBirthdate = (value) => {
  if (!value) return "";

  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const normalizeGender = (value) => {
  const normalized = String(value || "").trim().toUpperCase();

  if (["MALE", "FEMALE", "CUSTOM"].includes(normalized)) {
    return normalized;
  }

  return "MALE";
};

const getGenderPronoun = (genderName) => {
  if (genderName === "MALE") return "HIS";
  if (genderName === "FEMALE") return "HER";
  if (genderName === "CUSTOM") return "THEIRS";

  return "DEFAULT";
};

const toPhoneInputValue = (source) => {
  const phoneNumber = firstDefined(source.phoneNumber, source.phone);
  if (!phoneNumber) return "";

  const cleanedPhone = String(phoneNumber).replace(/[^\d+]/g, "");
  if (cleanedPhone.startsWith("+")) return cleanedPhone;

  const suffix = String(firstDefined(source.suffix, "961")).replace(/\D/g, "");
  const normalizedPhone = cleanedPhone.startsWith(suffix)
    ? cleanedPhone.slice(suffix.length)
    : cleanedPhone;

  return `+${suffix || "961"}${normalizedPhone}`;
};

const getPhonePayload = (phoneNumber) => {
  const normalized = String(phoneNumber || "").replace(/\s+/g, "");
  const parsedPhone = normalized ? parsePhoneNumber(normalized) : null;

  if (parsedPhone?.countryCallingCode && parsedPhone?.nationalNumber) {
    return {
      phoneNumber: parsedPhone.nationalNumber,
      suffix: parsedPhone.countryCallingCode,
    };
  }

  const withoutPlus = normalized.replace(/^\+/, "");

  if (withoutPlus.startsWith("961")) {
    return {
      phoneNumber: withoutPlus.slice(3),
      suffix: "961",
    };
  }

  return {
    phoneNumber: withoutPlus,
    suffix: "961",
  };
};

const getEmployeeUserId = (employee, fallbackUserId = "") => {
  const source = employee?.raw || employee || {};

  return (
    firstDefined(
      source.userId,
      source.userID,
      source.id,
      employee?.userID,
      employee?.id,
      fallbackUserId,
    ) || ""
  );
};

const mergeEmployeeDetails = (employee, detailsPayload) => {
  const baseSource = employee?.raw || employee || {};
  const detailsSource =
    firstDefined(
      detailsPayload?.user,
      detailsPayload?.profile,
      detailsPayload?.userDetails?.user,
      detailsPayload,
    ) || {};

  return {
    ...(employee || {}),
    raw: {
      ...baseSource,
      ...detailsSource,
    },
  };
};

function VendorEditUser() {
  useTranslationSnapshot();
  const location = useLocation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const isPreview = location.pathname.includes("-preview");
  const userManagementPath = isPreview
    ? "/vendor-user-management-preview"
    : "/vendor-user-management";
  const [form, setForm] = useState({
    birthdate: "",
    email: "",
    firstName: "",
    gender: "MALE",
    lastName: "",
    roleID: "",
  });
  const [phoneNumber, setPhoneNumber] = useState("");
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState("");
  const [currentRoleName, setCurrentRoleName] = useState("");
  const [selectedUserID, setSelectedUserID] = useState(userId || "");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const updateField = (field) => (event) => {
    const value =
      field === "firstName" || field === "lastName"
        ? Utils.sanitizeNameInput(event.target.value)
        : event.target.value;

    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const hydrateUser = (employee) => {
    const source = employee?.raw || employee || {};
    const nameParts = String(firstDefined(employee?.name, source.name, ""))
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const firstName = firstDefined(
      source.firstName,
      source.firstname,
      employee?.firstName,
      nameParts[0],
    );
    const lastName = firstDefined(
      source.lastName,
      source.lastname,
      employee?.lastName,
      nameParts.slice(1).join(" "),
    );
    const roleName = firstDefined(source.roleName, employee?.roleName, employee?.role);
    const roleID = firstDefined(source.roleID, source.roleId, employee?.roleID);

    setSelectedUserID(
      firstDefined(source.userId, source.userID, employee?.userID, employee?.id, userId) ||
        "",
    );
    setCurrentRoleName(roleName || "");
    setPhoneNumber(toPhoneInputValue(source) || toPhoneInputValue(employee || {}));
    setForm((currentForm) => ({
      ...currentForm,
      birthdate:
        toDateInput(
          firstDefined(
            source.birthdate,
            source.birthDate,
            source.dateOfBirth,
            employee?.birthdate,
          ),
        ) || currentForm.birthdate,
      email: firstDefined(source.email, employee?.email, currentForm.email) || "",
      firstName: firstDefined(firstName, currentForm.firstName) || "",
      gender: normalizeGender(
        firstDefined(source.gender?.genderName, source.genderName, source.gender),
      ),
      lastName: firstDefined(lastName, currentForm.lastName) || "",
      roleID: isUuid(roleID) ? roleID : currentForm.roleID,
    }));
  };

  useEffect(() => {
    let isCurrentRequest = true;

    const loadEditData = async () => {
      setLoading(true);
      setRolesLoading(true);
      setError("");
      setRolesError("");

      try {
        const routedUser = location.state?.user;
        let selectedUser = routedUser || null;
        if (routedUser) hydrateUser(routedUser);

        const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

        if (!companyId) {
          throw new Error("Company id was not found for this user.");
        }

        const rolesResponse = await roleController.getAllRoles({
          Authorization,
          application: 3,
          companyId,
        });

        if (rolesResponse?.isAxiosError || rolesResponse?.response) {
          throw rolesResponse;
        }

        const rolesPayload = unwrapPayload(rolesResponse);
        const roleList = Array.isArray(rolesPayload)
          ? rolesPayload
          : Array.isArray(rolesPayload?.content)
            ? rolesPayload.content
            : [];

        if (isCurrentRequest) {
          setRoles(
            roleList.filter((roleItem) => roleItem?.roleID && roleItem?.roleName),
          );
        }

        if (!routedUser && userId) {
          const usersResponse = await companyController.getCompanyEmployees({
            Authorization,
            companyId,
            page: 1,
            search: userId,
            size: 10,
          });

          if (usersResponse?.isAxiosError || usersResponse?.response) {
            throw usersResponse;
          }

          const usersPayload = unwrapPayload(usersResponse);
          const usersContent = Array.isArray(usersPayload?.content)
            ? usersPayload.content
            : Array.isArray(usersPayload)
              ? usersPayload
              : [];
          selectedUser =
            usersContent.find(
              (employee) => employee?.userId === userId || employee?.userID === userId,
            ) || usersContent[0];

          if (selectedUser) hydrateUser(selectedUser);
        }

        const selectedUserDetailsId = getEmployeeUserId(selectedUser, userId);

        if (selectedUserDetailsId) {
          const userDetailsResponse = await userController.getUser({
            jwt: Authorization,
            userID: selectedUserDetailsId,
          });

          if (!userDetailsResponse?.isAxiosError && !userDetailsResponse?.response) {
            hydrateUser(
              mergeEmployeeDetails(
                selectedUser,
                unwrapPayload(userDetailsResponse),
              ),
            );
          }
        }
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load user details.",
        );
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
          setRolesLoading(false);
        }
      }
    };

    loadEditData();

    return () => {
      isCurrentRequest = false;
    };
  }, [location.state, userId]);

  useEffect(() => {
    if (form.roleID || !currentRoleName || roles.length === 0) return;

    const matchedRole = roles.find(
      (roleItem) => normalizeLabel(roleItem.roleName) === normalizeLabel(currentRoleName),
    );

    if (matchedRole?.roleID) {
      setForm((currentForm) => ({
        ...currentForm,
        roleID: matchedRole.roleID,
      }));
    }
  }, [currentRoleName, form.roleID, roles]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      if (
        !Utils.checkName(form.firstName.trim()) ||
        !Utils.checkName(form.lastName.trim())
      ) {
        throw new Error("First name and last name can only contain letters.");
      }

      const Authorization = userService.getJwt();
      const companyID = userService.getUserData().Compid;
      const adminID = getStoredUserId();
      const phonePayload = getPhonePayload(phoneNumber);

      if (!companyID) {
        throw new Error("Company id was not found for this user.");
      }
      if (!adminID) {
        throw new Error("Admin id was not found for this user.");
      }
      if (!isUuid(selectedUserID)) {
        throw new Error("User id must be a valid UUID.");
      }
      if (!form.birthdate) {
        throw new Error("Birth date is required.");
      }
      if (!phonePayload.phoneNumber) {
        throw new Error("Phone number is required.");
      }
      if (rolesLoading) {
        throw new Error("Roles are still loading.");
      }
      if (!isUuid(form.roleID)) {
        throw new Error("Role id must be a valid UUID.");
      }

      const response = await companyController.addCompanyEmployees({
        Authorization,
        addCompanyEmloyeeRequest: {
          adminID,
          birthdate: toApiBirthdate(form.birthdate),
          companyID,
          gender: {
            costumGenderName: form.gender === "CUSTOM" ? "Other" : null,
            genderName: form.gender,
            pronoun: getGenderPronoun(form.gender),
          },
          roleID: form.roleID,
          userID: selectedUserID,
          userRegister: {
            businessDescription: null,
            businessName: null,
            businessProfileCategoryId: null,
            businessProfileTypeId: null,
            countryOfResidence: "Lebanon",
            email: form.email.trim(),
            firstname: form.firstName.trim(),
            lastname: form.lastName.trim(),
            password: null,
            phoneNumber: phonePayload.phoneNumber,
            suffix: phonePayload.suffix,
            userType: "END_USER",
          },
        },
      });

      if (response?.isAxiosError || response?.response) {
        throw response;
      }

      setShowSuccessModal(true);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to update user.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VendorLayout activeKey="users">
      <section className="vendor-add-user-page vendor-edit-user-page">
        <Link className="vendor-add-user-back" to={userManagementPath}>
          {translateLabel("txt_web_vendor_back_to_user_management", "Back to User Management")}
        </Link>

        <div className="vendor-add-user-heading">
          <div className="vendor-add-user-title">{translateLabel("txt_web_vendor_edit_user_details", "Edit User Details")}</div>
          <div className="vendor-add-user-subtitle">
            {translateLabel("txt_web_vendor_update_user_assignments", "Update user information and role assignments")}
          </div>
        </div>

        <form className="vendor-add-user-form" onSubmit={handleSubmit}>
          <div className="vendor-add-user-card">
            <div className="vendor-add-user-card-heading">
              <div className="vendor-add-user-card-title"> {translateLabel("txt_common_personal_information", "Personal Information")} </div>
              <div className="vendor-add-user-card-subtitle">
                {translateLabel("txt_web_vendor_update_basic_user_details", "Update the user's basic details")}
              </div>
            </div>

            <div className="vendor-add-user-grid">
              <label className="vendor-add-user-field">
                <span> {translateLabel("input_first_name", "First Name")}<i>*</i>
                </span>
                <div>
                  <LuUserRound aria-hidden="true" />
                  <input
                    type="text"
                    value={form.firstName}
                    aria-label={translateLabel("input_first_name", "First Name")}
                    required
                    disabled={loading || isSubmitting}
                    onChange={updateField("firstName")}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("input_last_name", "Last Name")}<i>*</i>
                </span>
                <div>
                  <LuUserRound aria-hidden="true" />
                  <input
                    type="text"
                    value={form.lastName}
                    aria-label={translateLabel("input_last_name", "Last Name")}
                    required
                    disabled={loading || isSubmitting}
                    onChange={updateField("lastName")}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("input_email_address", "Email Address")}<i>*</i>
                </span>
                <div>
                  <LuMail aria-hidden="true" />
                  <input
                    type="email"
                    value={form.email}
                    aria-label={translateLabel("input_email_address", "Email Address")}
                    required
                    disabled={loading || isSubmitting}
                    onChange={updateField("email")}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("input_phone_number", "Phone Number")}<i>*</i>
                </span>
                <div className="vendor-add-user-phone-input">
                  <PhoneInput
                    defaultCountry="LB"
                    international
                    countryCallingCodeEditable={false}
                    value={phoneNumber}
                    aria-label={translateLabel("input_phone_number", "Phone Number")}
                    disabled={loading || isSubmitting}
                    onChange={setPhoneNumber}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("txt_web_vendor_birth_date", "Birth date")}<i>*</i>
                </span>
                <div>
                  <input
                    className="vendor-add-user-date-input"
                    type="date"
                    value={form.birthdate}
                    aria-label={translateLabel("txt_web_vendor_birth_date", "Birth date")}
                    required
                    disabled={loading || isSubmitting}
                    onChange={updateField("birthdate")}
                  />
                </div>
              </label>

              <label className="vendor-add-user-field">
                <span> {translateLabel("input_gender", "Gender")}<i>*</i>
                </span>
                <div className="vendor-add-user-select">
                  <LuUsersRound aria-hidden="true" />
                  <select
                    value={form.gender}
                    aria-label={translateLabel("input_gender", "Gender")}
                    required
                    disabled={loading || isSubmitting}
                    onChange={updateField("gender")}
                  >
                    <option value="MALE">{translateLabel("txt_end_user_male", "Male")}</option>
                    <option value="FEMALE">{translateLabel("txt_end_user_female", "Female")}</option>
                    <option value="CUSTOM">{translateLabel("txt_end_user_custom", "Custom")}</option>
                  </select>
                  <LuChevronDown aria-hidden="true" />
                </div>
              </label>

              <label className="vendor-add-user-field vendor-edit-user-role-field">
                <span> {translateLabel("txt_web_vendor_role", "Role")}<i>*</i>
                </span>
                <div className="vendor-add-user-select">
                  <LuUsersRound aria-hidden="true" />
                  <select
                    value={form.roleID}
                    aria-label={translateLabel("txt_web_vendor_role", "Role")}
                    required
                    disabled={loading || rolesLoading || isSubmitting}
                    onChange={updateField("roleID")}
                  >
                    <option value="">
                      {rolesLoading ? translateLabel("txt_web_vendor_loading_roles", "Loading roles...") : translateLabel("txt_web_vendor_select_role", "Select role")}
                    </option>
                    {roles.map((roleItem) => (
                      <option value={roleItem.roleID} key={roleItem.roleID}>
                        {roleItem.roleName}
                      </option>
                    ))}
                  </select>
                  <LuChevronDown aria-hidden="true" />
                </div>
              </label>
            </div>

            <div className="vendor-add-user-actions">
              {error && <div className="vendor-add-user-error" role="alert">{translateEditUserFeedback(error)}</div>}
              {rolesError && (
                <div className="vendor-add-user-error" role="alert">{translateEditUserFeedback(rolesError)}</div>
              )}
              <button
                className="vendor-add-user-create"
                type="submit"
                disabled={loading || isSubmitting || rolesLoading}
              >
                {isSubmitting ? translateLabel("txt_end_user_saving", "Saving...") : translateLabel("btn_save_changes", "Save Changes")}
              </button>
              <Link
                className="vendor-add-user-cancel"
                to={userManagementPath}
              > {translateLabel("btn_cancel", "Cancel")} </Link>
            </div>
          </div>
        </form>

        {showSuccessModal && (
          <div className="vendor-add-user-success-backdrop">
            <div
              className="vendor-add-user-success-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="vendor-edit-user-success-title"
            >
              <div className="vendor-add-user-success-icon">
                <UserAddedSuccessIcon />
                <ActiveIcon />
              </div>
              <div
                className="vendor-add-user-success-title"
                id="vendor-edit-user-success-title"
              >
                {translateLabel("txt_web_vendor_user_details_updated", "User Details Updated Successfully")}
              </div>
              <button
                type="button"
                onClick={() => navigate(userManagementPath)}
              > {translateLabel("btn_continue", "Continue")} </button>
            </div>
          </div>
        )}
      </section>
    </VendorLayout>
  );
}

export default VendorEditUser;
